<template>
  <div class="sp-quantum-select-shell" :class="rootModifierClass">
    <div v-if="showNavbar" class="navbar">
      <transition name="navbar-leading-fade">
        <sp-button
          v-if="onBack"
          :disabled="disabled"
          class="navbar__leading-action"
          icon="chevron-left-filled"
          color="surface"
          size="small"
          @click="handleBack"
        ></sp-button>
      </transition>
      <div v-if="showTitleBar" class="navbar__title" :class="titleModifierClass">{{ title }}</div>
      <div v-if="showTitleBar" class="navbar__actions">
        <slot name="navbar-actions" />
      </div>
    </div>
    <div class="before-content-slot">
      <slot name="before-content" />
    </div>
    <div class="content">
      <slot />
    </div>
  </div>
</template>

<script setup>
/**
 * Outer chrome shared by every navigation container inside `SpQuantumSelect`.
 * Owns root structural CSS, the appearance modifier, size constraints,
 * breakpoint modifier, the navbar with optional title and built-in back
 * button (rendered when `on-back` is set), and the `before-content` plus
 * default slots.
 *
 * Private nested piece; not a globally-registered custom element.
 */
import { computed } from "vue";
import { appearanceOptions } from "#ce/components/SpQuantumSelect/props.js";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  disabled: booleanAttribute(),
  maxHeight: {
    type: String,
    default: "var(--quantum-select-popup-max-height)",
  },
  appearance: {
    type: String,
    default: "default",
    validator: (value) => appearanceOptions.includes(value),
  },
  title: {
    type: String,
    default: undefined,
  },
  hideTitleBar: booleanAttribute(),
  noSheet: booleanAttribute(),
  onBack: {
    type: Function,
    default: undefined,
  },
});

function handleBack() {
  if (!toBoolean(props.disabled)) {
    props.onBack?.();
  }
}

const showTitleBar = computed(() => Boolean(props.title) && !toBoolean(props.hideTitleBar));
const hasLeadingAction = computed(() => Boolean(props.onBack));
const showNavbar = computed(() => showTitleBar.value || hasLeadingAction.value);

const titleModifierClass = computed(() => ({ "--has-leading-action": hasLeadingAction.value }));

const { ltModifiers } = useBreakpoints();

const rootModifierClass = computed(() => {
  const modifiers = [...ltModifiers.value, `--appearance-${props.appearance}`];

  if (toBoolean(props.noSheet)) {
    modifiers.push("--no-sheet");
  }

  return modifiers;
});
</script>

<style scoped lang="scss">
.sp-quantum-select-shell {
  display: block;
  position: relative;
  height: 100%;
  overflow: hidden;
  margin-inline: var(--sp-comp-quantum-select-shell-margin-inline, 0);
}

.sp-quantum-select-shell:not(.--appearance-inline) {
  /* SpPopup clips its content to `--sp-popup-available-height`, and the panel scrolls its own list.
     A shell taller than that budget engages both scrollers, so the dropdown shows two scrollbars.
     The fallback covers the hosts that publish no budget: permanent and no-menu. */
  --shell-height-budget: min(v-bind(maxHeight), var(--sp-popup-available-height, 100vh));

  min-height: min(var(--quantum-select-popup-min-height), var(--shell-height-budget));
  min-width: var(--quantum-select-popup-width);

  height: 100%;
  max-height: var(--shell-height-budget);

  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);

  /* Below `sm` the shell fills the bottom sheet. A popup kept by `no-sheet` keeps its budget. */
  &.--lt-sm-screen:not(.--no-sheet) {
    max-height: 100%;
    min-height: 0;
  }
}

.--appearance-inline {
  --sp-comp-popup-min-width: auto !important;
  --quantum-select-popup-width: auto !important;
}

.navbar {
  --navbar-gap: var(--sp-comp-quantum-select-navbar-gap, 0.25rem);
  display: flex;
  gap: var(--navbar-gap);
  padding-inline: var(--quantum-select-content-padding);
  min-height: var(--sp-comp-quantum-select-navbar-min-height, 2.5rem);
  margin-top: var(--sp-comp-quantum-select-navbar-margin-top, 0.25rem);
  align-items: center;
  position: relative;
  overflow: hidden;
}

.navbar__leading-action {
  --sp-comp-button-focus-opacity: 0;
  --sp-comp-button-focus-visible-opacity: 1;
  --sp-comp-button-bg-color: linear-gradient(to right, white 66%, transparent 100%);

  width: var(--sp-comp-quantum-select-navbar-leading-width, 2rem);
  height: 100%;
  display: flex;
  align-items: center;
  position: absolute;
  z-index: 9;
}

.navbar__title {
  font-family: var(--sp-sys-font-family-bold);
  font-weight: var(--sp-sys-font-weight-bold);
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  transition: transform var(--quantum-select-animation-time, 0.3s) ease-in-out;
}

/* Not a flex item itself, so a title bar with nothing slotted pays no gap; the title's full width
   already pushes slotted actions to the end. */
.navbar__actions {
  display: contents;
}

.navbar__title.--has-leading-action {
  transform: translateX(var(--sp-comp-quantum-select-navbar-leading-width, 2rem));
}

.navbar-leading-fade-enter-from,
.navbar-leading-fade-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.navbar-leading-fade-enter-active,
.navbar-leading-fade-leave-active {
  transition: all var(--quantum-select-animation-time, 0.3s) ease-in-out;
}

.content {
  /* Pin to the grid's 1fr row: the navbar is conditional, and without the
     explicit placement the content auto-places into an `auto` row, collapsing
     to its own height and leaving the fr row empty below it. */
  grid-row: 3;
  padding-top: var(--quantum-select-content-padding-top, 0.5rem);
  padding-bottom: var(--quantum-select-content-padding-bottom, 0.5rem);
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
}

/* The insets separate chrome from list content; while the panel renders
   its centered loading state, drop them so the indicator centers on the
   dropdown's full height. Keyed to the rendered state itself, so it stays
   correct however the panel arrived at the loading branch. When an in-panel
   search box renders (sheet mode), the inset stays — it spaces the search box,
   and the indicator centers in the area below it. */
.content:has(.sp-quantum-select-panel__loading):not(:has(sp-search-box)) {
  padding-block: 0;
}

.navbar + .before-content-slot + .content {
  padding: 0;
}
</style>
